import styled from "styled-components/macro";
import { darkGrey, defaultMetricGrey } from "../../constants/colors";
import {
  BREAKPOINT_MOBILE,
  FONT_SIZE_EXTRA_SMALL,
  FONT_SIZE_MEDIUM,
  FONT_SIZE_LARGE,
} from "../../constants/constants";
import { HtmlTooltip } from "reports/components/simple/HtmlTooltip";
import { useIsAdmin } from "utils/customHooks/genericHooks";
import { Tooltip } from "@mui/material";
import { useShouldFetchFromAskr } from "./useShouldFetchFromAskr";
import {
  useFlightConfig,
  useReportConfig,
} from "components/DataGetter/FlightConfig/useFlightConfig";
import { MetricToPropsMapping } from "reports/utils/metricUtils";

const SummaryMetricStyles = styled.div<{ $numMetrics: number }>`
  display: flex;
  justify-items: center;

  .metrics {
    display: flex;
    padding: 0em 2em;

    .singleMetric {
      padding: 0 2em;
      text-align: center;
    }
    .metricName {
      justify-content: center;
      font-size: 17px;
      color: ${darkGrey};
      padding-bottom: 5px;
    }

    .metricValue {
      font-size: ${FONT_SIZE_LARGE}px;
      font-weight: 700;
    }
  }

  @media print {
    .metrics {
      padding: 00px 10px;
      .metricValue {
        font-size: ${FONT_SIZE_MEDIUM - 4}px;
      }

      .metricName,
      .metricsSymbol {
        font-size: ${FONT_SIZE_EXTRA_SMALL}px;
      }
    }
  }

  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    grid-template-columns: 1fr;
    grid-template-rows: auto auto;

    .metrics {
      display: grid;
      grid-column-start: 1;
      grid-row-gap: 10px;
      grid-template-columns: repeat(
        ${props => (props.$numMetrics > 3 ? 3 : props.$numMetrics)},
        minmax(50px, 1fr)
      );
      padding: 1em 10px 0em;

      .singleMetric {
        padding: 0 10px;
      }

      .metricValue {
        font-size: ${FONT_SIZE_MEDIUM};
      }
    }
  }
`;

const DEFAULT_COLORS = { defaultMetricGrey };

export type SummaryMetricType = {
  name: string;
  nameTranslated: string;
  shortName?: string;
  numericValue: number | null;
  stringFormattedValue?: string;
  unit?: string;
  color?: string;
  decimalPoints?: number;
  maxNumChars?: number;
  description?: string;
};

export default function SummaryMetricsRow({
  metricsList,
}: {
  metricsList: SummaryMetricType[];
}) {
  const isAdminUser = useIsAdmin();
  const reportConfig = useReportConfig();
  const [{ metricDescriptions }] = useFlightConfig();
  const allSummaryMetricsRows = [
    ...(reportConfig?.eosReportMetricsConfig?.summaryMetricsRow1 ?? []),
    ...(reportConfig?.eosReportMetricsConfig?.summaryMetricsRow2 ?? []),
  ];

  const { shouldFetchFromAskr } = useShouldFetchFromAskr(true);
  return (
    <SummaryMetricStyles $numMetrics={metricsList.length}>
      <div className="metrics">
        {metricsList.map(
          (
            {
              name,
              nameTranslated,
              numericValue,
              stringFormattedValue,
              unit = "",
              color = "",
              description,
            },
            idx
          ) => {
            const thisMetricConfig = allSummaryMetricsRows.find(
              metric => metric.name === name
            );
            const dataSource = isAdminUser
              ? `${
                  shouldFetchFromAskr ? "askr" : "midgard"
                }:pnlBreakdown:${thisMetricConfig?.dataDumpKey}`
              : "";
            const dataDumpKeyFromName = MetricToPropsMapping[name]?.dataDumpKey;

            const tooltipTitle =
              description ?? metricDescriptions[dataDumpKeyFromName] ?? "";

            return (
              <HtmlTooltip
                key={`${name}-${idx}`}
                title={tooltipTitle}
                placement="top"
              >
                <div className="singleMetric">
                  <div className="metricName">{nameTranslated ?? name}</div>
                  <Tooltip title={dataSource}>
                    <div
                      className="metricValue"
                      style={{
                        color: DEFAULT_COLORS[color] ?? defaultMetricGrey,
                      }}
                    >
                      {stringFormattedValue
                        ? stringFormattedValue
                        : numericValue === null
                          ? "--"
                          : numericValue}
                    </div>
                  </Tooltip>
                  <div
                    className="metricsSymbol"
                    style={{
                      color: DEFAULT_COLORS[color] ?? defaultMetricGrey,
                    }}
                  >
                    {numericValue !== null ? unit : ""}
                  </div>
                </div>
              </HtmlTooltip>
            );
          }
        )}
      </div>
    </SummaryMetricStyles>
  );
}
